"use client";

import React from "react";
import { UserPlus, FileCheck, Wallet, ArrowRight, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  title: "The Discovery Call",
  description: "A 15-20 minute conversation with Olivia, our AI voice agent. She learns your workflows, team structure, and current bottlenecks.",
  icon: UserPlus,
  benefits: ["Zero prep required", "15-minute commitment"]
}, {
  title: "AI Analysis Engine",
  description: "Our proprietary AI processes your data to identify high-impact automation opportunities across your 5-50 employee operation.",
  icon: FileCheck,
  benefits: ["Deep process mapping", "48-hour turnaround"]
}, {
  title: "The Recoup Report",
  description: "Receive a personalized roadmap identifying exactly where you can save hours. Your personalized assessment roadmap.",
  icon: Wallet,
  benefits: ["Full implementation strategy", "Actionable AI roadmap"]
}];
export default function WhatYouGet() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: timelineRef,
    isVisible: isTimelineVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224768" id="what-you-get" className="bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 rounded-lg blur-[120px] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        <div ref={headerRef} className={`text-center max-w-2xl mx-auto mb-20 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="mb-6 border-primary/30 text-primary bg-primary/5 px-4 py-1.5 text-sm font-default">
            The Assessment Process
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold font-default mb-6 tracking-tight text-foreground">
            We Find Your <span className="text-primary">Hidden Hours</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-8 font-default">
            A premium consulting experience designed for busy founders. We identify the specific AI integrations that will scale your business without increasing headcount.
          </p>
          <Badge variant="default" className="text-base px-6 py-2 shadow-md shadow-primary/10 bg-primary text-primary-foreground font-default">
            Financial Impact: Identified In Your Report
          </Badge>
        </div>

        <div ref={timelineRef} className={`relative transition-all duration-1000 delay-200 ${isTimelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, i) => {
            const Icon = step.icon;
            const isHighlight = i === 2;
            return <div key={i} data-index={i} className="relative flex flex-col items-center justify-start group h-full">
                  <div className="relative mb-6 w-12 h-12 rounded-md bg-background border border-primary/50 flex items-center justify-center z-10 shadow-md transition-transform duration-500 group-hover:scale-110">
                    <Icon className="size-5 text-primary" />
                  </div>

                  <div className="w-full h-full">
                    <Card className={`bg-card/40 backdrop-blur-md border-border transition-all duration-300 ease-out hover:-translate-y-2 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/5 overflow-hidden relative h-full flex flex-col ${isHighlight ? "border-primary/60 ring-1 ring-primary/20" : ""}`}>
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      <CardHeader className="relative z-10 text-center">
                        <div className="flex flex-col items-center gap-2 mb-2">
                          <span className="text-5xl font-black text-white/10 font-mono transition-colors duration-300 group-hover:text-white/20">
                            0{i + 1}
                          </span>
                          <CardTitle className="text-2xl text-foreground font-default">
                            {step.title}
                          </CardTitle>
                        </div>
                        <CardDescription className="text-base text-muted-foreground font-default">
                          {step.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="relative z-10 mt-auto">
                        <ul className="flex flex-col gap-2 items-center justify-center">
                          {step.benefits.map((benefit, j) => <li key={j} data-index={j} className="flex items-center gap-2 text-sm text-muted-foreground/80 font-default">
                              <CircleCheck className="size-4 text-primary" />
                              <span>{benefit}</span>
                            </li>)}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-24 text-center">
          <Button size="lg" className="rounded-md px-8 h-14 text-lg font-semibold group bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 transition-all font-default">
            Book Your AI Assessment
            <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
          </Button>
          <p className="mt-4 text-sm text-muted-foreground font-default">
            Limited to 5 assessments per week to ensure premium delivery.
          </p>
        </div>
      </div>
    </section>;
}